import React from 'react';
import { FileSearch, AlertTriangle, X } from 'lucide-react';
import { Icon } from '@ideoz/ui';

export interface AttachedFile {
  id: string;
  file: File;
  preview?: string; // For image files
  isImage: boolean;
  isUploading?: boolean;
  uploadProgress?: number;
  error?: string;
}

export interface FilePreviewProps {
  files: AttachedFile[];
  onDelete?: (fileId: string) => void;
  hasDelete?: boolean;
  className?: string;
}

const FilePreview: React.FC<FilePreviewProps> = ({
  files,
  onDelete,
  hasDelete = true,
  className = '',
}) => {
  if (files.length === 0) return null;

  return (
    <div className={`w-full ${className}`}>
      <div className="flex flex-wrap gap-2 pb-2">
        {files.map((attachedFile) => {
          // Calculate size based on number of files
          const isSingleFile = files.length === 1;
          const sizeClasses = isSingleFile ? 'max-w-[400px] h-16' : 'max-w-[200px] h-14';
          const imagesSizes = isSingleFile ? 'h-16 w-16' : 'h-16 w-16';
          const textSizes = isSingleFile ? 'text-base' : 'text-sm';

          // Get file extension
          const fileName = attachedFile.file.name;
          const lastDotIndex = fileName.lastIndexOf('.');
          const fileExtension =
            lastDotIndex > 0 ? fileName.substring(lastDotIndex + 1).toUpperCase() : '';

          return (
            <div key={attachedFile.id} className="relative">
              {attachedFile.isImage && attachedFile.preview ? (
                // Image files: show only image with delete button and hover overlay
                <div
                  className={`relative group ${imagesSizes} rounded-lg overflow-hidden border border-gray-200`}
                >
                  <img
                    src={attachedFile.preview}
                    alt={attachedFile.file.name}
                    className="w-full h-full object-cover transition-transform duration-200"
                  />

                  {/* Upload overlay */}
                  {attachedFile.isUploading && (
                    <div className="absolute top-0 right-0  h-full w-full bg-black bg-opacity-20 flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}

                  {/* Error overlay */}
                  {attachedFile.error && (
                    <div className="absolute inset-0 bg-red-500 bg-opacity-50 flex items-center justify-center">
                      <Icon icon={AlertTriangle} size={24} strokeWidth={2} color="#D92D20" />
                    </div>
                  )}

                  {/* Hover overlay */}
                  {!attachedFile.isUploading && !attachedFile.error && (
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg"></div>
                  )}

                  {/* Fixed delete button for images */}
                  {hasDelete && onDelete && (
                    <button
                      onClick={() => onDelete(attachedFile.id)}
                      className="absolute top-1 right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-shadow z-10"
                      type="button"
                      hidden={attachedFile.isUploading}
                    >
                      <Icon icon={X} size={14} strokeWidth={2.5} color="#667085" />
                    </button>
                  )}
                </div>
              ) : (
                // Non-image files: show icon, name and type
                <div
                  className={`relative h-16 bg-gray-50 border border-gray-200 rounded-lg p-2 flex gap-1 justify-center ${sizeClasses} hover:bg-gray-100 transition-colors ${attachedFile.isUploading ? 'opacity-75' : ''} ${attachedFile.error ? 'border-red-300 bg-red-50' : ''}`}
                >
                  {/* File icon or loading spinner */}
                  <div className="mb-1">
                    {attachedFile.isUploading ? (
                      <div className="absolute top-0 right-0  h-full w-full bg-black bg-opacity-20 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : attachedFile.error ? (
                      <Icon icon={AlertTriangle} size={24} strokeWidth={2} color="#D92D20" />
                    ) : (
                      <Icon icon={FileSearch} size={24} strokeWidth={2} color="#029B8C" />
                    )}
                  </div>

                  {/* File name with extension */}
                  <div className="flex flex-col max-w-[90%]">
                    <p
                      className={`${textSizes} font-medium ${attachedFile.error ? 'text-red-700' : 'text-gray-700'} text-start truncate max-w-[95%]`}
                    >
                      {attachedFile.file.name}
                    </p>

                    {/* File type or upload status */}
                    {attachedFile.error ? (
                      <p className="text-xs text-red-600 text-start">Upload failed</p>
                    ) : fileExtension ? (
                      <p className="text-xs text-gray-500 text-start">{fileExtension}</p>
                    ) : null}
                  </div>

                  {/* Fixed delete button for documents */}
                  {hasDelete && onDelete && (
                    <button
                      onClick={() => onDelete(attachedFile.id)}
                      className="absolute top-1 right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
                      type="button"
                      hidden={attachedFile.isUploading}
                    >
                      <Icon icon={X} size={14} strokeWidth={2.5} color="#667085" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FilePreview;
